/**
 * Work-pipeline failures, turned into sentences an operator can act on.
 *
 * Modelled on `features/quotes/errors.ts` and re-authored rather than imported,
 * because the vocabularies genuinely differ: this module's 422s are about FILES
 * and TEST ROUNDS, and a shared helper would have to grow a branch per feature
 * until nobody could tell which sentence any surface would produce.
 *
 * The rule both files keep: a 403 never names the permission key. A key is a word
 * for administrators, and the person reading this needs to know who to ask.
 */

import { ApiRequestError, ApiTransportError } from '../../lib/api';

export function workErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That work order belongs to another country.';
    }
    if (error.status === 404) return 'That work order does not exist.';
    if (error.status === 403) {
      return 'This account is not allowed to do that in this country.';
    }
    /*
     * 409 and 422 are PASSED THROUGH. The API's own wording on these is the most
     * precise thing available — "Work order 12 is not in testing", "Only .tdl,
     * .pdf and .zip files can be attached" — and each already names the state or
     * the rule it found. Replacing them with a house sentence would lose the one
     * detail that tells the operator what to do next.
     */
    if (error.status === 409 || error.status === 422) return error.message;
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}
